import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import { showError } from '../../../../core/alert/sweetAlertConfig';
import { all_routes } from '../../../router/all_routes';
import CollapseHeader from '../../../../core/common/collapse-header';
import VehicleKeyModule from './VehicleKeyModule';

export interface VehicleDTO {
  vehicleId: string;
  vehicleType: string;
  vehicleBrand: string;
  vehicleModel: string;
  numberPlate: string;
  vehicleStatus: string;
}

export interface KeyDTO {
  id: string;
  keyCode: string;
  doorId: string | null;
  roomId: string | null;
  roomName: string | null;
  floorId: string | null;
  floorName: string | null;
  buildingId: string | null;
  buildingName: string | null;
  vehicleId: string | null;
  vehicleNumberPlate: string | null;
  noOfKeys: number;
  separatedKeys: number | 0;
  bundles: { count: number }[] | 0;
  responsibleParty: string | null;
  isRestricted: boolean;
  handoverStatus: string | null;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
}

interface GetVehiclesResponse {
  data: VehicleDTO[];
}

const navItems = [
  { label: 'Key Allocations', icon: 'ti ti-settings', link: all_routes.keyAllocations, permission: 'ROLE_GET_KEY_ALLOCATIONS' },
  { label: 'Add Keys', icon: 'ti ti-settings', link: all_routes.keys, permission: 'ROLE_ADD_LOCATION' },
  { label: 'Add Vehicle Keys', icon: 'ti ti-car', link: all_routes.vehiclekey, permission: 'ROLE_ADD_KEY' },
];

const VehicleKeys: React.FC = () => {
  const locationHook = useLocation();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState<VehicleDTO[]>([]);
  const [keyCounts, setKeyCounts] = useState<Record<string, number>>({});
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const checkToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Authentication token not found. Please log in.');
      navigate('/login');
      return false;
    }
    return token;
  };

  const fetchVehiclesAndKeys = async () => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_GET_VEHICLES')) {
      showError('You do not have permission to view vehicles.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const payload = {};
      const res: AxiosResponse = await apiDefinitions.getVehicles(1, payload);
      if (res.status === 200) {
        const data: GetVehiclesResponse = res.data;
        setVehicles(data.data || []);
        if ((data.data || []).length === 0) setError('No vehicles found. Please add a vehicle first.');

        // Fetch keys for each vehicle
        const keyPromises = (data.data || []).map(vehicle =>
          apiDefinitions.getKeysByVehicleId(vehicle.vehicleId)
            .then(keyRes => {
              if (keyRes.status === 200 && keyRes.data.status === 'S1000') {
                const keys: KeyDTO[] = keyRes.data.data || [];
                const totalKeys = keys.reduce((sum, key) => sum + key.noOfKeys, 0);
                return { vehicleId: vehicle.vehicleId, total: totalKeys };
              }
              return { vehicleId: vehicle.vehicleId, total: 0 };
            })
            .catch(e => {
              return { vehicleId: vehicle.vehicleId, total: 0 };
            })
        );
        const keyResults = await Promise.all(keyPromises);
        const keyCountMap = keyResults.reduce((acc, { vehicleId, total }) => ({ ...acc, [vehicleId]: total }), {});
        setKeyCounts(keyCountMap);
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch vehicles');
      }
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      } else showError('Failed to fetch vehicles. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVehiclesAndKeys();
  }, []);

  const handleVehicleClick = (vehicleId: string) => {
    setSelectedVehicleId(vehicleId);
    setShowModal(true);
  };



  const VehicleCard: React.FC<{ vehicle: VehicleDTO }> = ({ vehicle }) => (
    <div
      className={`card h-100 cursor-pointer border border-2 ${vehicle.vehicleStatus === 'AVAILABLE' ? 'bg-success bg-opacity-25 border-success' : 'bg-danger bg-opacity-25 border-danger'} vehicle-card`}
      onClick={() => handleVehicleClick(vehicle.vehicleId)}
      title={`${vehicle.vehicleBrand} ${vehicle.vehicleModel}`}
    >
      <div className="card-body py-2 px-3 d-flex justify-content-between align-items-center">
        <span className="fw-semibold text-dark" style={{ fontSize: '1.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '65%' }}>
          {vehicle.numberPlate} ({vehicle.vehicleBrand} {vehicle.vehicleModel})
        </span>
        <span className="badge bg-dark rounded-pill" title={`${keyCounts[vehicle.vehicleId] || 0} key(s)`}>
          <i className="ti ti-key me-1"></i>{keyCounts[vehicle.vehicleId] || 0}
        </span>
      </div>
    </div>
  );

  return (
    <div className="page-wrapper">
      <style>{`
        .vehicle-card {
          min-height: 80px;
          border-radius: 0.75rem;
          width: 100%;
        }
        .vehicle-card .card-body {
          line-height: 1.2;
          padding: 1rem;
        }
        .vehicle-card .badge {
          font-size: 1rem;
          padding: 0.4rem 0.8rem;
        }
        @media (max-width: 767px) {
          .vehicle-card {
            min-height: 70px;
          }
          .vehicle-card .card-body {
            font-size: 1rem;
          }
          .vehicle-card .badge {
            font-size: 0.9rem;
            padding: 0.3rem 0.6rem;
          }
        }
        @media (min-width: 768px) and (max-width: 991px) {
          .vehicle-card {
            min-height: 75px;
          }
          .vehicle-card .card-body {
            font-size: 1.05rem;
          }
          .vehicle-card .badge {
            font-size: 0.95rem;
          }
        }
      `}</style>

      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Key Management/Key Dasboard/Add Vehicle Key</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body pb-0 pt-2">
                <ul className="nav nav-tabs nav-tabs-bottom" style={{ gap: '12px', display: 'flex' }}>
                  {navItems.map((item, i) =>
                    item.permission && checkPermission(item.permission) ? (
                      <li className="nav-item me-item" key={i}>
                        <Link to={item.link} className={`nav-link px-0 ${locationHook.pathname === item.link ? 'active' : ''}`}>
                          <i className={item.icon}></i> {item.label}
                        </Link>
                      </li>
                    ) : null
                  )}
                </ul>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h3 className="text-center mb-4">Vehicles</h3>
                {isLoading ? (
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger" role="alert">{error}</div>
                ) : vehicles.length === 0 ? (
                  <div className="alert alert-info" role="alert">No vehicles found. Please add a vehicle first.</div>
                ) : (
                  <div className="row g-3 align-items-stretch">
                    {vehicles.map(vehicle => (
                      <div key={vehicle.vehicleId} className="col-12 col-sm-6 col-md-4 col-lg-3">
                        <VehicleCard vehicle={vehicle} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {selectedVehicleId && (
              <VehicleKeyModule
                show={showModal}
                onHide={() => {
                  setShowModal(false);
                  setSelectedVehicleId(null);
                }}
                vehicleId={selectedVehicleId}
                numberPlate={vehicles.find(v => v.vehicleId === selectedVehicleId)?.numberPlate || ''}
                onKeyAdded={fetchVehiclesAndKeys}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleKeys;